/**
 * Filtros do layout A + C.
 *
 * `<FacetFilters label groups value onChange id? onClear? />` — grupos de caixas de marcar (multisseleção) com
 * contagem, no painel de contexto:
 * - `groups`: `{ id, title, options: { value, label, count? }[] }[]`;
 * - `value`: o que está marcado, por grupo (`{ estado: ['Running', 'Pending'] }`); grupo ausente = nada marcado;
 * - `onChange(next, groupId)`: o objeto inteiro já atualizado;
 * - cada grupo é um fieldset recolhível (o botão da legenda tem `aria-expanded`); aberto/fechado fica lembrado por
 *   pessoa (`useRemembered`, chave `facets.<id>.<grupo>`; `id` padrão: o `label`);
 * - mais de 8 opções: as 8 primeiras (e as marcadas) e "Mostrar mais N"; mais de 12: busca dentro do grupo;
 * - "Limpar filtros" no fim quando há algo marcado (`onClear`, padrão: `onChange({}, '')`).
 * A contagem fica em fonte mono à direita. As etiquetas removíveis dos filtros ativos ficam na `ListToolbar`.
 *
 * `<QuickChips label items value onChange extra />` — chips de filtro rápido com contagem ("Sem agente (61)"):
 * - `items`: `{ id, label, count? }[]`; `value`: o id ativo (ou lista de ids); `onChange(id)` ao clicar;
 * - `extra`: algo a mais no fim da linha (ex.: botão "+ Filtro").
 *
 * `<ViewToggle label value options onChange />` — alternância genérica de visão ("Tabela | Mapa de pressão",
 * "Mapa | Árvore | Lista"), botões com `aria-pressed`. Regra: a lista que a tela já tinha continua sendo uma opção —
 * marque-a com `list: true` (sem nenhuma, o console avisa). Para lembrar a escolha por pessoa e por tela, use
 * `const [view, setView] = useViewChoice('assets.view', ['table', 'pressure'], 'table')` e passe `value`/`onChange`.
 */
import { useI18n, type CommonKey } from '@hive/i18n';
import { useEffect, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './icons';
import { cx } from './layout';
import { useRemembered } from './prefs';

export interface FacetOption {
  value: string;
  label: ReactNode;
  count?: number | string | null;
}

export interface FacetGroup {
  id: string;
  title: ReactNode;
  options: FacetOption[];
}

export type FacetValue = Record<string, string[]>;

/** Quantos valores estão marcados no total (o "n" de "Filtros (n)"). */
export function facetCount(v: FacetValue | null | undefined): number {
  return Object.values(v ?? {}).reduce((a, x) => a + (x?.length ?? 0), 0);
}

/** O valor sem um item (para a etiqueta removível). */
export function facetWithout(v: FacetValue, group: string, value: string): FacetValue {
  return { ...v, [group]: (v[group] ?? []).filter((x) => x !== value) };
}

const FACET_PAGE = 8;
const FACET_SEARCH = 12;
const text = (n: ReactNode, fallback: string) => (typeof n === 'string' || typeof n === 'number' ? String(n) : fallback);

export function FacetFilters({
  groups,
  value,
  onChange,
  label,
  id,
  onClear,
  className,
}: {
  groups: FacetGroup[];
  value: FacetValue;
  onChange: (next: FacetValue, groupId: string) => void;
  label: string;
  /** Prefixo da chave que lembra os grupos abertos (padrão: o `label`). */
  id?: string;
  onClear?: () => void;
  className?: string;
}) {
  const { t } = useI18n<CommonKey>();
  const any = facetCount(value) > 0;
  return (
    <div className={cx('facets', className)} role="group" aria-label={label}>
      {groups.map((g) => (
        <FacetGroupView key={g.id} g={g} value={value} onChange={onChange} memo={`facets.${id ?? label}.${g.id}`} />
      ))}
      {any && (
        <button type="button" className="btn btn-sm btn-ghost facet-clear" onClick={() => (onClear ? onClear() : onChange({}, ''))}>
          {t('ui.facet.clear')}
        </button>
      )}
    </div>
  );
}

function FacetGroupView({ g, value, onChange, memo }: { g: FacetGroup; value: FacetValue; onChange: (next: FacetValue, groupId: string) => void; memo: string }) {
  const { t, fmt } = useI18n<CommonKey>();
  const [open, setOpen] = useRemembered<boolean>(memo, true, (v) => typeof v === 'boolean');
  const [more, setMore] = useState(false);
  const [q, setQ] = useState('');
  const on = value[g.id] ?? [];
  const title = text(g.title, g.id);
  const toggle = (v: string, checked: boolean) => {
    const next = checked ? [...on.filter((x) => x !== v), v] : on.filter((x) => x !== v);
    onChange({ ...value, [g.id]: next }, g.id);
  };
  const needle = q.trim().toLowerCase();
  const matching = needle ? g.options.filter((o) => `${text(o.label, o.value)} ${o.value}`.toLowerCase().includes(needle)) : g.options;
  // recolhida a lista longa: as primeiras e as marcadas (marcada nunca some)
  const cut = !needle && !more && matching.length > FACET_PAGE;
  const shown = cut ? matching.filter((o, i) => i < FACET_PAGE || on.includes(o.value)) : matching;
  return (
    <fieldset className={cx('facet', !open && 'closed')}>
      <legend>
        <button type="button" className="facet-toggle" aria-expanded={open} aria-label={title} onClick={() => setOpen(!open)}>
          <Icon name={open ? 'chev' : 'chevR'} size="sm" />
          <span className="facet-title">{g.title}</span>
          {on.length > 0 && (
            <span className="facet-sel" aria-hidden="true">
              {fmt.number(on.length)}
            </span>
          )}
        </button>
      </legend>
      {open && (
        <>
          {g.options.length > FACET_SEARCH && (
            <input type="search" className="facet-search" aria-label={t('ui.facet.search', { group: title })} placeholder={t('ui.facet.search', { group: title })} value={q} onChange={(e) => setQ(e.target.value)} />
          )}
          {shown.map((o) => (
            <label className="facet-opt" key={o.value}>
              <input type="checkbox" checked={on.includes(o.value)} onChange={(e) => toggle(o.value, e.target.checked)} />
              <span className="facet-label" title={text(o.label, o.value)}>
                {o.label}
              </span>
              {o.count != null && o.count !== '' && <span className="facet-count">{typeof o.count === 'number' ? fmt.number(o.count) : o.count}</span>}
            </label>
          ))}
          {needle && !matching.length && <span className="facet-none">{t('ui.facet.none')}</span>}
          {!needle && matching.length > FACET_PAGE && (
            <button type="button" className="facet-more" aria-expanded={more} onClick={() => setMore(!more)}>
              {more ? t('ui.facet.less') : t('ui.facet.more', { n: fmt.number(matching.length - shown.length) })}
            </button>
          )}
        </>
      )}
    </fieldset>
  );
}

export interface QuickChip {
  id: string;
  label: ReactNode;
  count?: number | null;
}

export function QuickChips({
  items,
  value,
  onChange,
  label,
  extra,
  className,
}: {
  items: QuickChip[];
  value?: string | string[] | null;
  onChange: (id: string) => void;
  label: string;
  extra?: ReactNode;
  className?: string;
}) {
  const { fmt } = useI18n<CommonKey>();
  const on = (id: string) => (Array.isArray(value) ? value.includes(id) : value === id);
  return (
    <div className={cx('qchips', className)} role="group" aria-label={label}>
      {items.map((c) => (
        <button key={c.id} type="button" className={cx('qchip', on(c.id) && 'on')} aria-pressed={on(c.id)} onClick={() => onChange(c.id)}>
          {c.label}
          {c.count != null && (
            <>
              {' '}
              <span className="qchip-count">({fmt.number(c.count)})</span>
            </>
          )}
        </button>
      ))}
      {extra}
    </div>
  );
}

export interface ViewOption<T extends string = string> {
  id: T;
  label: ReactNode;
  icon?: IconName;
  /** A visão de lista/tabela (sempre presente). */
  list?: boolean;
}

/** Visão escolhida, lembrada por pessoa e tela (`key` = "tela.view"); opção que deixou de existir volta à `fallback`. */
export function useViewChoice<T extends string>(key: string, ids: readonly T[], fallback: T): [T, (v: T) => void] {
  return useRemembered<T>(key, fallback, (v) => typeof v === 'string' && (ids as readonly string[]).includes(v));
}

export function ViewToggle<T extends string = string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T;
  options: ViewOption<T>[];
  onChange: (id: T) => void;
  label: string;
  className?: string;
}) {
  const hasList = options.some((o) => o.list);
  useEffect(() => {
    if (!hasList) console.warn(`ViewToggle "${label}": a visão de lista precisa continuar como opção (marque-a com list: true).`);
  }, [hasList, label]);
  return (
    <div className={cx('view-toggle', className)} role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={o.id === value} className={cx(o.id === value && 'on')} onClick={() => onChange(o.id)}>
          {o.icon && <Icon name={o.icon} size="sm" />}
          {o.label}
        </button>
      ))}
    </div>
  );
}
